import React, { createContext, useContext, useReducer } from 'react';
import orderExtractionApi from '../services/api/orderExtractionApi';

// Initial state
const initialState = {
  // Extraction data
  currentExtraction: null,
  extractionData: null,
  selectedProducts: {},
  
  // UI state
  showSidebar: true,
  selectedFileIndex: null,
  
  // Dialog states
  showContextViewer: false,
  showReprocessDialog: false,
  isReprocessing: false,
  
  // Loading states
  isCreatingExtraction: false,
  isUploadingFiles: false,
  isProcessing: false,
  processingStep: '',
  
  // Form data
  selectedFiles: [],
  textInput: '',
  customerId: '',
  
  // History
  extractions: [],
  
  // Polling state
  pollingInterval: null,
  isPolling: false
};

// Action types
const ActionTypes = {
  // Extraction actions
  SET_CURRENT_EXTRACTION: 'SET_CURRENT_EXTRACTION',
  SET_EXTRACTION_DATA: 'SET_EXTRACTION_DATA',
  UPDATE_EXTRACTION_DATA: 'UPDATE_EXTRACTION_DATA',
  
  // Selected products actions
  SET_SELECTED_PRODUCTS: 'SET_SELECTED_PRODUCTS',
  ADD_SELECTED_PRODUCT: 'ADD_SELECTED_PRODUCT',
  REMOVE_SELECTED_PRODUCT: 'REMOVE_SELECTED_PRODUCT',
  CLEAR_SELECTED_PRODUCTS: 'CLEAR_SELECTED_PRODUCTS',
  
  // UI actions
  SET_SIDEBAR_VISIBILITY: 'SET_SIDEBAR_VISIBILITY',
  SET_SELECTED_FILE_INDEX: 'SET_SELECTED_FILE_INDEX',
  
  // Dialog actions
  SET_CONTEXT_VIEWER: 'SET_CONTEXT_VIEWER',
  SET_REPROCESS_DIALOG: 'SET_REPROCESS_DIALOG',
  SET_REPROCESSING: 'SET_REPROCESSING',
  
  // Loading actions
  SET_CREATING_EXTRACTION: 'SET_CREATING_EXTRACTION',
  SET_UPLOADING_FILES: 'SET_UPLOADING_FILES',
  SET_PROCESSING: 'SET_PROCESSING',
  SET_PROCESSING_STEP: 'SET_PROCESSING_STEP',
  
  // Form actions
  SET_SELECTED_FILES: 'SET_SELECTED_FILES',
  SET_TEXT_INPUT: 'SET_TEXT_INPUT',
  SET_CUSTOMER_ID: 'SET_CUSTOMER_ID',
  CLEAR_FORM: 'CLEAR_FORM',
  
  // History actions
  SET_EXTRACTIONS: 'SET_EXTRACTIONS',
  ADD_EXTRACTION: 'ADD_EXTRACTION',
  
  // Polling actions
  SET_POLLING_INTERVAL: 'SET_POLLING_INTERVAL',
  SET_POLLING: 'SET_POLLING'
};

// Reducer
const orderExtractionReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_CURRENT_EXTRACTION:
      return { ...state, currentExtraction: action.payload };
      
    case ActionTypes.SET_EXTRACTION_DATA:
      return { ...state, extractionData: action.payload };
      
    case ActionTypes.UPDATE_EXTRACTION_DATA:
      return {
        ...state,
        extractionData: {
          ...state.extractionData,
          ...action.payload
        }
      };
      
    case ActionTypes.SET_SELECTED_PRODUCTS:
      return { ...state, selectedProducts: action.payload };
      
    case ActionTypes.ADD_SELECTED_PRODUCT:
      return {
        ...state,
        selectedProducts: {
          ...state.selectedProducts,
          [action.payload.itemIndex]: action.payload.product
        }
      };
      
    case ActionTypes.REMOVE_SELECTED_PRODUCT:
      const newSelections = { ...state.selectedProducts };
      delete newSelections[action.payload];
      return { ...state, selectedProducts: newSelections };
      
    case ActionTypes.CLEAR_SELECTED_PRODUCTS:
      return { ...state, selectedProducts: {} };
      
    case ActionTypes.SET_SIDEBAR_VISIBILITY:
      return { ...state, showSidebar: action.payload };
      
    case ActionTypes.SET_SELECTED_FILE_INDEX:
      return { ...state, selectedFileIndex: action.payload };
      
    case ActionTypes.SET_CONTEXT_VIEWER:
      return { ...state, showContextViewer: action.payload };
      
    case ActionTypes.SET_REPROCESS_DIALOG:
      return { ...state, showReprocessDialog: action.payload };
      
    case ActionTypes.SET_REPROCESSING:
      return { ...state, isReprocessing: action.payload };
      
    case ActionTypes.SET_CREATING_EXTRACTION:
      return { ...state, isCreatingExtraction: action.payload };
      
    case ActionTypes.SET_UPLOADING_FILES:
      return { ...state, isUploadingFiles: action.payload };
      
    case ActionTypes.SET_PROCESSING:
      return { ...state, isProcessing: action.payload };
      
    case ActionTypes.SET_PROCESSING_STEP:
      return { ...state, processingStep: action.payload };
      
    case ActionTypes.SET_SELECTED_FILES:
      return { ...state, selectedFiles: action.payload };
      
    case ActionTypes.SET_TEXT_INPUT:
      return { ...state, textInput: action.payload };
      
    case ActionTypes.SET_CUSTOMER_ID:
      return { ...state, customerId: action.payload };
      
    case ActionTypes.CLEAR_FORM:
      return {
        ...state,
        selectedFiles: [],
        textInput: '',
        customerId: ''
      };
      
    case ActionTypes.SET_EXTRACTIONS:
      return { ...state, extractions: action.payload };
      
    case ActionTypes.ADD_EXTRACTION:
      return {
        ...state,
        extractions: [action.payload, ...state.extractions]
      };
      
    case ActionTypes.SET_POLLING_INTERVAL:
      return { ...state, pollingInterval: action.payload };
      
    case ActionTypes.SET_POLLING:
      return { ...state, isPolling: action.payload };
      
    default:
      return state;
  }
};

// Context
const OrderExtractionContext = createContext();

// Provider component
export const OrderExtractionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(orderExtractionReducer, initialState);

  // Helper function to process extraction data and populate selected products
  const processExtractionData = (data) => {
    if (!data || !data.extractedOrderItems) return data;
    
    const initialSelectedProducts = {};
    data.extractedOrderItems.forEach((item, index) => {
      if (item.selectedProductCode) {
        const candidate = item.candidates?.find(c => c.productCode === item.selectedProductCode);
        const productDescription = candidate?.productDescription || item.selectedProductCode;
        
        initialSelectedProducts[index] = {
          productCode: item.selectedProductCode,
          productDescription: productDescription,
          quantity: item.quantity,
          unityOfMeasure: item.unityOfMeasure,
          observation: item.observation
        };
      }
    });
    
    dispatch({ type: ActionTypes.SET_SELECTED_PRODUCTS, payload: initialSelectedProducts });
    return data;
  };

  // Actions
  const actions = {
    // Extraction actions
    setCurrentExtraction: (extraction) => 
      dispatch({ type: ActionTypes.SET_CURRENT_EXTRACTION, payload: extraction }),
      
    setExtractionData: (data) => 
      dispatch({ type: ActionTypes.SET_EXTRACTION_DATA, payload: data }),
      
    updateExtractionData: (updates) => 
      dispatch({ type: ActionTypes.UPDATE_EXTRACTION_DATA, payload: updates }),
      
    // Selected products actions
    setSelectedProducts: (products) => 
      dispatch({ type: ActionTypes.SET_SELECTED_PRODUCTS, payload: products }),
      
    addSelectedProduct: (itemIndex, product) => 
      dispatch({ type: ActionTypes.ADD_SELECTED_PRODUCT, payload: { itemIndex, product } }),
      
    removeSelectedProduct: (itemIndex) => 
      dispatch({ type: ActionTypes.REMOVE_SELECTED_PRODUCT, payload: itemIndex }),
      
    clearSelectedProducts: () => 
      dispatch({ type: ActionTypes.CLEAR_SELECTED_PRODUCTS }),
      
    // UI actions
    setSidebarVisibility: (visible) => 
      dispatch({ type: ActionTypes.SET_SIDEBAR_VISIBILITY, payload: visible }),
      
    setSelectedFileIndex: (index) => 
      dispatch({ type: ActionTypes.SET_SELECTED_FILE_INDEX, payload: index }),
      
    // Dialog actions
    setContextViewer: (open) => 
      dispatch({ type: ActionTypes.SET_CONTEXT_VIEWER, payload: open }),
      
    setReprocessDialog: (open) => 
      dispatch({ type: ActionTypes.SET_REPROCESS_DIALOG, payload: open }),
      
    setReprocessing: (processing) => 
      dispatch({ type: ActionTypes.SET_REPROCESSING, payload: processing }),
      
    // Loading actions
    setCreatingExtraction: (creating) => 
      dispatch({ type: ActionTypes.SET_CREATING_EXTRACTION, payload: creating }),
      
    setUploadingFiles: (uploading) => 
      dispatch({ type: ActionTypes.SET_UPLOADING_FILES, payload: uploading }),
      
    setProcessing: (processing) => 
      dispatch({ type: ActionTypes.SET_PROCESSING, payload: processing }),
      
    setProcessingStep: (step) => 
      dispatch({ type: ActionTypes.SET_PROCESSING_STEP, payload: step }),
      
    // Form actions
    setSelectedFiles: (files) => 
      dispatch({ type: ActionTypes.SET_SELECTED_FILES, payload: files }),
      
    setTextInput: (text) => 
      dispatch({ type: ActionTypes.SET_TEXT_INPUT, payload: text }),
      
    setCustomerId: (id) => 
      dispatch({ type: ActionTypes.SET_CUSTOMER_ID, payload: id }),
      
    clearForm: () => 
      dispatch({ type: ActionTypes.CLEAR_FORM }),
      
    // History actions
    setExtractions: (extractions) => 
      dispatch({ type: ActionTypes.SET_EXTRACTIONS, payload: extractions }),
      
    addExtraction: (extraction) => 
      dispatch({ type: ActionTypes.ADD_EXTRACTION, payload: extraction }),
      
    // Polling actions
    setPollingInterval: (interval) => 
      dispatch({ type: ActionTypes.SET_POLLING_INTERVAL, payload: interval }),
      
    setPolling: (polling) => 
      dispatch({ type: ActionTypes.SET_POLLING, payload: polling }),
      
    // API actions
    loadExtractionHistory: async () => {
      try {
        const response = await orderExtractionApi.listOrderExtractions();
        dispatch({ type: ActionTypes.SET_EXTRACTIONS, payload: response.items || [] });
      } catch (error) {
        console.error('Error loading extraction history:', error);
        throw error;
      }
    },
    
    selectExtraction: async (extraction) => {
      try {
        const fullDetails = await orderExtractionApi.getOrderExtractionById(extraction.id);
        const processedData = processExtractionData(fullDetails);
        dispatch({ type: ActionTypes.SET_CURRENT_EXTRACTION, payload: processedData });
        dispatch({ type: ActionTypes.SET_EXTRACTION_DATA, payload: processedData });
        dispatch({ type: ActionTypes.SET_SELECTED_FILE_INDEX, payload: null });
        dispatch({ type: ActionTypes.SET_SIDEBAR_VISIBILITY, payload: false });
        
        // Start polling if extraction is pending
        if (processedData.status !== 'ocr_processed') {
          actions.startPolling(processedData.id);
        }
      } catch (error) {
        console.error('Error loading extraction details:', error);
        throw error;
      }
    },
    
    createExtraction: async (customerId, files) => {
      dispatch({ type: ActionTypes.SET_CREATING_EXTRACTION, payload: true });
      try {
        const createResponse = await orderExtractionApi.createOrderExtraction(customerId, files);
        
        dispatch({ type: ActionTypes.SET_UPLOADING_FILES, payload: true });
        dispatch({ type: ActionTypes.SET_PROCESSING_STEP, payload: 'Enviando arquivos...' });
        
        const uploadPromises = files.map((file, index) => 
          orderExtractionApi.uploadFileToPresignedUrl(createResponse.filesUploadUrls[index], file)
        );
        
        await Promise.all(uploadPromises);
        
        dispatch({ type: ActionTypes.SET_UPLOADING_FILES, payload: false });
        dispatch({ type: ActionTypes.SET_PROCESSING, payload: true });
        dispatch({ type: ActionTypes.SET_PROCESSING_STEP, payload: 'Processando arquivos com IA...' });
        
        const extractionResponse = await orderExtractionApi.startOrderExtractionProcess(
          createResponse.orderExtractionId
        );
        
        const processedData = processExtractionData(extractionResponse);
        dispatch({ type: ActionTypes.SET_EXTRACTION_DATA, payload: processedData });
        dispatch({ type: ActionTypes.SET_CURRENT_EXTRACTION, payload: processedData });
        dispatch({ type: ActionTypes.CLEAR_FORM });
        
        // Reload history
        await actions.loadExtractionHistory();
        
        // Start polling if extraction is pending
        if (processedData.status !== 'ocr_processed') {
          actions.startPolling(processedData.id);
        }
        
        return processedData;
      } catch (error) {
        console.error('Error creating extraction:', error);
        throw error;
      } finally {
        dispatch({ type: ActionTypes.SET_CREATING_EXTRACTION, payload: false });
        dispatch({ type: ActionTypes.SET_UPLOADING_FILES, payload: false });
        dispatch({ type: ActionTypes.SET_PROCESSING, payload: false });
        dispatch({ type: ActionTypes.SET_PROCESSING_STEP, payload: '' });
      }
    },
    
    updateItemQuantity: async (extractionId, itemId, quantity) => {
      try {
        await orderExtractionApi.updateItemQuantity(extractionId, itemId, quantity);
        
        // Update local state
        const currentData = state.extractionData;
        if (currentData && currentData.extractedOrderItems) {
          const updatedItems = currentData.extractedOrderItems.map(item => 
            item.id === itemId ? { ...item, quantity } : item
          );
          
          dispatch({
            type: ActionTypes.UPDATE_EXTRACTION_DATA,
            payload: { extractedOrderItems: updatedItems }
          });
          
          // Update selected products if this item is selected
          const itemIndex = currentData.extractedOrderItems.findIndex(item => item.id === itemId);
          if (state.selectedProducts[itemIndex]) {
            dispatch({
              type: ActionTypes.ADD_SELECTED_PRODUCT,
              payload: {
                itemIndex,
                product: { ...state.selectedProducts[itemIndex], quantity }
              }
            });
          }
        }
      } catch (error) {
        console.error('Error updating quantity:', error);
        throw error;
      }
    },
    
    setItemIgnored: async (extractionId, itemId, ignored) => {
      try {
        await orderExtractionApi.setItemIgnored(extractionId, itemId, ignored);
        
        // Update local state
        const currentData = state.extractionData;
        if (currentData && currentData.extractedOrderItems) {
          const updatedItems = currentData.extractedOrderItems.map(item => 
            item.id === itemId ? { ...item, ignored } : item
          );
          
          dispatch({
            type: ActionTypes.UPDATE_EXTRACTION_DATA,
            payload: { extractedOrderItems: updatedItems }
          });
          
          // Remove from selected products if ignored
          if (ignored) {
            const itemIndex = currentData.extractedOrderItems.findIndex(item => item.id === itemId);
            if (state.selectedProducts[itemIndex]) {
              dispatch({ type: ActionTypes.REMOVE_SELECTED_PRODUCT, payload: itemIndex });
            }
          }
        }
      } catch (error) {
        console.error('Error updating ignored status:', error);
        throw error;
      }
    },
    
    selectProductCode: async (extractionId, itemId, productCode) => {
      try {
        await orderExtractionApi.selectProductCode(extractionId, itemId, productCode);
        
        // Update local state
        const currentData = state.extractionData;
        if (currentData && currentData.extractedOrderItems) {
          const updatedItems = currentData.extractedOrderItems.map(item => 
            item.id === itemId ? { ...item, selectedProductCode: productCode } : item
          );
          
          dispatch({
            type: ActionTypes.UPDATE_EXTRACTION_DATA,
            payload: { extractedOrderItems: updatedItems }
          });
          
          // Add to selected products
          const itemIndex = currentData.extractedOrderItems.findIndex(item => item.id === itemId);
          const item = currentData.extractedOrderItems[itemIndex];
          const candidate = item.candidates?.find(c => c.productCode === productCode);
          
          if (candidate) {
            dispatch({
              type: ActionTypes.ADD_SELECTED_PRODUCT,
              payload: {
                itemIndex,
                product: {
                  productCode: candidate.productCode,
                  productDescription: candidate.productDescription,
                  quantity: item.quantity,
                  unityOfMeasure: item.unityOfMeasure,
                  observation: item.observation
                }
              }
            });
          }
        }
      } catch (error) {
        console.error('Error selecting product:', error);
        throw error;
      }
    },
    
    reprocessExtraction: async (extractionId, additionalContext) => {
      dispatch({ type: ActionTypes.SET_REPROCESSING, payload: true });
      try {
        const response = await orderExtractionApi.startOrderExtractionProcess(
          extractionId,
          additionalContext
        );
        
        const processedData = processExtractionData(response);
        dispatch({ type: ActionTypes.SET_EXTRACTION_DATA, payload: processedData });
        dispatch({ type: ActionTypes.SET_CURRENT_EXTRACTION, payload: processedData });
        dispatch({ type: ActionTypes.SET_REPROCESS_DIALOG, payload: false });
        
        // Start polling if extraction is pending after reprocessing
        if (processedData.status !== 'ocr_processed') {
          actions.startPolling(processedData.id);
        }
        
        return processedData;
      } catch (error) {
        console.error('Error reprocessing:', error);
        throw error;
      } finally {
        dispatch({ type: ActionTypes.SET_REPROCESSING, payload: false });
      }
    },
    
    // Polling functions
    startPolling: (extractionId) => {
      // Clear any existing polling
      if (state.pollingInterval) {
        clearInterval(state.pollingInterval);
      }
      
      const interval = setInterval(async () => {
        try {
          const updatedExtraction = await orderExtractionApi.getOrderExtractionById(extractionId);
          
          // Check if current extraction has changed
          const currentExtraction = state.currentExtraction;
          const hasCurrentExtractionChanged = currentExtraction && 
            currentExtraction.id === extractionId && 
            (currentExtraction.status !== updatedExtraction.status ||
             currentExtraction.updatedAt !== updatedExtraction.updatedAt ||
             JSON.stringify(currentExtraction.extractedOrderItems) !== JSON.stringify(updatedExtraction.extractedOrderItems));
          
          // Update current extraction only if it changed
          if (hasCurrentExtractionChanged) {
            dispatch({ type: ActionTypes.SET_CURRENT_EXTRACTION, payload: updatedExtraction });
            dispatch({ type: ActionTypes.SET_EXTRACTION_DATA, payload: updatedExtraction });
          }
          
          // Check if extraction in history list has changed
          const currentHistoryExtraction = state.extractions.find(ext => ext.id === extractionId);
          const hasHistoryExtractionChanged = currentHistoryExtraction && 
            (currentHistoryExtraction.status !== updatedExtraction.status ||
             currentHistoryExtraction.updatedAt !== updatedExtraction.updatedAt);
          
          // Update history list only if extraction changed
          if (hasHistoryExtractionChanged) {
            const updatedExtractions = state.extractions.map(ext => 
              ext.id === extractionId ? updatedExtraction : ext
            );
            dispatch({ type: ActionTypes.SET_EXTRACTIONS, payload: updatedExtractions });
          }
          
          // Stop polling if extraction is no longer pending
          if (updatedExtraction.status == 'ocr_processed') {
            clearInterval(interval);
            dispatch({ type: ActionTypes.SET_POLLING_INTERVAL, payload: null });
            dispatch({ type: ActionTypes.SET_POLLING, payload: false });
          }
        } catch (error) {
          console.error('Error polling extraction status:', error);
        }
      }, 7000); // Poll every 7 seconds
      
      dispatch({ type: ActionTypes.SET_POLLING_INTERVAL, payload: interval });
      dispatch({ type: ActionTypes.SET_POLLING, payload: true });
    },
    
    stopPolling: () => {
      if (state.pollingInterval) {
        clearInterval(state.pollingInterval);
        dispatch({ type: ActionTypes.SET_POLLING_INTERVAL, payload: null });
        dispatch({ type: ActionTypes.SET_POLLING, payload: false });
      }
    }
  };

  const value = {
    state,
    actions,
    processExtractionData
  };

  return (
    <OrderExtractionContext.Provider value={value}>
      {children}
    </OrderExtractionContext.Provider>
  );
};

// Hook to use the context
export const useOrderExtraction = () => {
  const context = useContext(OrderExtractionContext);
  if (!context) {
    throw new Error('useOrderExtraction must be used within an OrderExtractionProvider');
  }
  return context;
};

export default OrderExtractionContext;
